import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
    useConfigList, 
    useConfigFile, 
    useConfigBackups,
    useSaveConfigFile, 
    useValidateConfig,
    useRestoreBackup
} from '../../../hooks/useApi';

/**
 * JSON 配置管理器
 * 用於管理 templates 和 config 目錄下的 JSON 設定檔
 */
const ConfigManager = () => {
    const { user } = useAuth();
    const [selectedFile, setSelectedFile] = useState(null);
    const [editedContent, setEditedContent] = useState('');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [expandedDirs, setExpandedDirs] = useState({});
    const [activeTab, setActiveTab] = useState('editor'); // editor, backups
    const [searchTerm, setSearchTerm] = useState('');

    // React Query hooks
    const { 
        data: directoriesResult, 
        isLoading: loadingDirs, 
        refetch: refetchDirs 
    } = useConfigList();

    const { 
        data: fileResult, 
        isLoading: loadingFile,
        refetch: refetchFile
    } = useConfigFile(selectedFile);

    const { 
        data: backupsResult, 
        isLoading: loadingBackups, 
        refetch: refetchBackups 
    } = useConfigBackups();

    const saveMutation = useSaveConfigFile();
    const validateMutation = useValidateConfig();
    const restoreMutation = useRestoreBackup();

    const directories = directoriesResult?.data || {};
    const fileContent = fileResult?.data;
    const backups = backupsResult?.data || [];
    const loading = loadingDirs || loadingFile;
    const saving = saveMutation.isPending;

    // 初始化展開狀態
    useEffect(() => {
        if (Object.keys(directories).length > 0) {
            const expanded = {};
            Object.keys(directories).forEach(key => {
                expanded[key] = true;
            });
            setExpandedDirs(prev => ({ ...expanded, ...prev }));
        }
    }, [directories]);

    // 當檔案內容載入時，更新編輯器內容
    useEffect(() => {
        if (fileContent) {
            if (fileContent.isValidJson) {
                setEditedContent(JSON.stringify(fileContent.content, null, 2));
            } else {
                setEditedContent(fileContent.rawContent);
            }
            setValidationResult(null);
        }
    }, [fileContent]);

    // 載入檔案內容
    const loadFile = async (filePath) => {
        setSelectedFile(filePath);
        setError(null);
    };

    // 儲存檔案
    const saveFile = async () => {
        if (!selectedFile) return;

        try {
            setError(null);
            setSuccessMessage(null);

            // 先驗證 JSON
            const validateResult = await validateMutation.mutateAsync(editedContent);
            
            if (!validateResult?.data?.valid) {
                setValidationResult(validateResult?.data);
                setError('JSON 格式錯誤: ' + validateResult?.data?.error);
                return;
            }

            // 儲存
            await saveMutation.mutateAsync({
                path: selectedFile,
                content: editedContent
            });

            setSuccessMessage('檔案儲存成功！');
            setValidationResult({ valid: true });
            // 重新載入檔案
            refetchFile();
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError('儲存檔案失敗: ' + (err.message || '未知錯誤'));
        }
    };

    // 還原備份
    const restoreBackup = async (backupPath) => {
        if (!confirm(`確定要還原此備份嗎？\n${backupPath}`)) return;

        try {
            const result = await restoreMutation.mutateAsync(backupPath);
            setSuccessMessage('備份還原成功！');
            refetchDirs();
            if (selectedFile === result?.data?.originalPath) {
                refetchFile();
            }
        } catch (err) {
            setError('還原備份失敗: ' + (err.message || '未知錯誤'));
        }
    };

    // 驗證 JSON
    const validateJson = async () => {
        try {
            const result = await validateMutation.mutateAsync(editedContent);
            setValidationResult(result?.data);

            if (result?.data?.valid) {
                // 格式化 JSON
                setEditedContent(JSON.stringify(result.data.parsed, null, 2));
            }
        } catch (err) {
            setError('驗證失敗: ' + (err.message || '未知錯誤'));
        }
    };

    // 切換目錄展開狀態
    const toggleDir = (path) => {
        setExpandedDirs(prev => ({
            ...prev,
            [path]: !prev[path]
        }));
    };

    // 檢查是否有管理員權限
    if (!user?.admin && !user?.roles?.includes('admin')) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <h2 className="text-xl font-bold text-red-800 mb-2">🔒 存取被拒絕</h2>
                    <p className="text-red-600">您需要管理員權限才能存取此頁面。</p>
                </div>
            </div>
        );
    }

    // 渲染目錄樹
    const renderTree = (items, level = 0) => {
        if (!items || items.length === 0) return null;

        return items
            .filter(item => {
                if (!searchTerm) return true;
                const term = searchTerm.toLowerCase();
                return item.name.toLowerCase().includes(term) || 
                       item.path.toLowerCase().includes(term);
            })
            .map((item) => (
                <div key={item.path} style={{ marginLeft: level * 16 }}>
                    {item.type === 'directory' ? (
                        <div>
                            <button
                                onClick={() => toggleDir(item.path)}
                                className="flex items-center w-full px-2 py-1 text-left hover:bg-gray-100 rounded"
                            >
                                <span className="mr-2">
                                    {expandedDirs[item.path] ? '📂' : '📁'}
                                </span>
                                <span className="font-medium text-gray-700">{item.name}</span>
                            </button>
                            {expandedDirs[item.path] && item.children && (
                                <div className="ml-2">
                                    {renderTree(item.children, level + 1)}
                                </div>
                            )}
                        </div>
                    ) : (
                        <button
                            onClick={() => loadFile(item.path)}
                            className={`flex items-center w-full px-2 py-1 text-left rounded transition-colors ${
                                selectedFile === item.path
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'hover:bg-gray-100 text-gray-600'
                            }`}
                        >
                            <span className="mr-2">📄</span>
                            <span className="truncate">{item.name}</span>
                        </button>
                    )}
                </div>
            ));
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* 標題 */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <span className="mr-2">⚙️</span>
                    系統配置管理
                </h1>
                <p className="text-gray-600 mt-1">
                    管理 templates 和 config 目錄下的 JSON 設定檔
                </p>
            </div>

            {/* Quick Links - Related Tools */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                <Link 
                    to="/dev" 
                    className="flex flex-col items-center p-3 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">🛠️</span>
                    <span className="text-sm font-medium text-orange-800">Dev Panel</span>
                </Link>
                <Link 
                    to="/settings" 
                    className="flex flex-col items-center p-3 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">🛠️</span>
                    <span className="text-sm font-medium text-orange-800">Settings</span>
                </Link>
                <Link 
                    to="/workflow-designer" 
                    className="flex flex-col items-center p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">🔧</span>
                    <span className="text-sm font-medium text-blue-800">Workflow Designer</span>
                </Link>
                <button
                    onClick={() => refetchDirs()}
                    className="flex flex-col items-center p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">🔄</span>
                    <span className="text-sm font-medium text-purple-800">Refresh Files</span>
                </button>
            </div>

            {/* Config File Types Help */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">📋 Configuration File Types</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                        <p className="font-medium text-blue-700">templates/*/form.json</p>
                        <p className="text-blue-600 text-xs">Form field definitions, validation rules</p>
                    </div>
                    <div>
                        <p className="font-medium text-blue-700">templates/*/route.json</p>
                        <p className="text-blue-600 text-xs">Approval workflow steps and conditions</p>
                    </div>
                    <div>
                        <p className="font-medium text-blue-700">config/*.json</p>
                        <p className="text-blue-600 text-xs">System settings, role definitions</p>
                    </div>
                </div>
            </div>

            {/* 標籤頁 */}
            <div className="flex space-x-2 mb-4">
                <button
                    onClick={() => setActiveTab('editor')}
                    className={`px-4 py-2 rounded-t-lg font-medium ${
                        activeTab === 'editor'
                            ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    📝 編輯器
                </button>
                <button
                    onClick={() => setActiveTab('backups')}
                    className={`px-4 py-2 rounded-t-lg font-medium ${
                        activeTab === 'backups'
                            ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    💾 備份管理
                </button>
            </div>

            {/* 錯誤和成功訊息 */}
            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">❌ {error}</p>
                    <button
                        onClick={() => setError(null)}
                        className="text-sm text-red-600 hover:underline mt-2"
                    >
                        關閉
                    </button>
                </div>
            )}

            {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800">✅ {successMessage}</p>
                </div>
            )}

            {activeTab === 'editor' && (
                <div className="flex gap-6">
                    {/* 左側：檔案樹 */}
                    <div className="w-80 flex-shrink-0">
                        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">📁 檔案結構</h3>
                                <button
                                    onClick={() => refetchDirs()}
                                    className="text-sm text-blue-600 hover:underline"
                                    disabled={loadingDirs}
                                >
                                    🔄 重新載入
                                </button>
                            </div>

                            {/* 搜尋 */}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="搜尋檔案..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* 目錄樹 */}
                            <div className="max-h-[calc(100vh-350px)] overflow-auto">
                                {loadingDirs && !Object.keys(directories).length ? (
                                    <p className="text-gray-500 text-center py-4">載入中...</p>
                                ) : (
                                    Object.entries(directories).map(([dirName, items]) => (
                                        <div key={dirName} className="mb-2">
                                            <button
                                                onClick={() => toggleDir(dirName)}
                                                className="flex items-center w-full px-2 py-1 text-left hover:bg-gray-100 rounded font-semibold text-gray-800"
                                            >
                                                <span className="mr-2">
                                                    {expandedDirs[dirName] ? '📂' : '📁'}
                                                </span>
                                                {dirName}
                                            </button>
                                            {expandedDirs[dirName] && (
                                                <div className="ml-2">
                                                    {renderTree(items, 1)}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 右側：編輯器 */}
                    <div className="flex-1">
                        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                            {selectedFile ? (
                                <>
                                    {/* 檔案資訊 */}
                                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                                        <div>
                                            <h3 className="font-semibold text-gray-900">
                                                📄 {fileContent?.name}
                                            </h3>
                                            <p className="text-sm text-gray-500">
                                                {selectedFile} | 
                                                {fileContent?.size ? ` ${(fileContent.size / 1024).toFixed(2)} KB` : ''} |
                                                {fileContent?.modifiedAt ? ` 修改於 ${new Date(fileContent.modifiedAt).toLocaleString('zh-TW')}` : ''}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {validationResult && (
                                                <span className={`px-2 py-1 rounded text-sm ${
                                                    validationResult.valid
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {validationResult.valid ? '✓ JSON 有效' : '✗ JSON 無效'}
                                                </span>
                                            )}
                                            <button
                                                onClick={validateJson}
                                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                                            >
                                                驗證 JSON
                                            </button>
                                            <button
                                                onClick={saveFile}
                                                disabled={saving}
                                                className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm font-medium"
                                            >
                                                {saving ? '儲存中...' : '💾 儲存'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* 編輯區域 */}
                                    <textarea
                                        value={editedContent}
                                        onChange={(e) => {
                                            setEditedContent(e.target.value);
                                            setValidationResult(null);
                                        }}
                                        className="w-full h-[calc(100vh-400px)] p-4 font-mono text-sm bg-gray-900 text-green-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                        spellCheck={false}
                                    />

                                    {/* 驗證錯誤提示 */}
                                    {validationResult && !validationResult.valid && (
                                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                            <p className="text-red-800 text-sm">
                                                <strong>錯誤：</strong> {validationResult.error}
                                            </p>
                                            {validationResult.position && (
                                                <p className="text-red-600 text-xs mt-1">
                                                    位置: 字元 {validationResult.position}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center justify-center h-[calc(100vh-350px)] text-gray-500">
                                    <div className="text-center">
                                        <p className="text-4xl mb-4">📄</p>
                                        <p>選擇左側的檔案開始編輯</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'backups' && (
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">💾 備份檔案列表</h3>
                        <button
                            onClick={() => refetchBackups()}
                            className="text-sm text-blue-600 hover:underline"
                        >
                            🔄 重新載入
                        </button>
                    </div>

                    {backups.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">目前沒有備份檔案</p>
                    ) : (
                        <div className="space-y-2 max-h-[calc(100vh-350px)] overflow-auto">
                            {backups.map((backup, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                >
                                    <div>
                                        <p className="font-medium text-gray-900">{backup.name}</p>
                                        <p className="text-sm text-gray-500">
                                            {backup.path} | {(backup.size / 1024).toFixed(2)} KB
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {new Date(backup.createdAt).toLocaleString('zh-TW')}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className={`px-2 py-1 rounded text-xs ${
                                            backup.type === 'deleted'
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {backup.type === 'deleted' ? '已刪除' : '備份'}
                                        </span>
                                        <button
                                            onClick={() => restoreBackup(backup.path)}
                                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                                        >
                                            還原
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ConfigManager;
